import React, { useState, useEffect } from 'react';
import { BrandLogo } from '../components/BrandLogo';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Youtube, Play, ArrowUpRight, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const CHANNEL_URL = 'https://www.youtube.com/@shifthappensworld';
const cleanTitle = (t) => (t || '').replace(/#\w+/g, '').trim();

const SubscribeButton = ({ testId }) => (
  <a
    href={`${CHANNEL_URL}?sub_confirmation=1`}
    target="_blank"
    rel="noopener noreferrer"
    data-testid={testId}
    className="inline-flex items-center gap-2 bg-[#C41E3A] hover:bg-[#A01830] text-white px-6 py-3 rounded-full font-semibold transition-colors"
  >
    <Youtube className="w-5 h-5" /> Subscribe on YouTube
  </a>
);

const Player = ({ video }) => (
  <div className={`mx-auto rounded-2xl overflow-hidden border border-zinc-800 bg-zinc-900 ${video.is_short ? 'aspect-[9/16] max-w-[340px]' : 'aspect-video w-full'}`}>
    <iframe
      key={video.youtube_id}
      src={`https://www.youtube-nocookie.com/embed/${video.youtube_id}?rel=0&modestbranding=1`}
      title={cleanTitle(video.title)}
      className="w-full h-full"
      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
      allowFullScreen
      data-testid="podcast-player"
    />
  </div>
);

const Thumb = ({ video, active, onSelect }) => (
  <button
    type="button"
    onClick={() => onSelect(video)}
    data-testid={`podcast-thumb-${video.youtube_id}`}
    className={`group text-left rounded-xl overflow-hidden border transition-colors ${active ? 'border-[#C41E3A]' : 'border-zinc-800 hover:border-zinc-600'}`}
  >
    <div className="relative aspect-video bg-zinc-900">
      <img src={video.thumbnail} alt={cleanTitle(video.title)} className="w-full h-full object-cover opacity-80 group-hover:opacity-100 transition-opacity" loading="lazy" />
      <span className="absolute inset-0 flex items-center justify-center"><Play className="w-8 h-8 text-white drop-shadow" /></span>
      {video.is_short && <span className="absolute top-2 left-2 text-[10px] uppercase tracking-wider bg-black/70 text-white px-2 py-0.5 rounded">Short</span>}
    </div>
    <p className="p-3 text-sm text-zinc-300 line-clamp-2">{cleanTitle(video.title)}</p>
  </button>
);

export default function PodcastPage() {
  const [videos, setVideos] = useState(null);
  const [current, setCurrent] = useState(null);

  useEffect(() => {
    axios.get(`${API_URL}/api/podcast-videos`)
      .then((r) => { setVideos(r.data.videos); setCurrent(r.data.videos[0] || null); })
      .catch(() => setVideos([]));
  }, []);

  return (
    <div className="min-h-screen bg-zinc-950 text-white" data-testid="podcast-page">
      <header className="fixed top-0 left-0 right-0 z-50 bg-zinc-950/90 backdrop-blur-md border-b border-zinc-800">
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/message-from-founder" className="flex items-center gap-2 text-zinc-400 hover:text-white transition-colors text-sm" data-testid="podcast-back-link">
            <ArrowLeft className="w-4 h-4" /> Founder's Message
          </Link>
          <BrandLogo className="h-12" />
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 pt-28 pb-20">
        <div className="flex flex-wrap items-end justify-between gap-6 mb-12">
          <div>
            <p className="text-[#C41E3A] text-xs tracking-[0.2em] uppercase mb-3">The Podcast</p>
            <h1 className="text-4xl sm:text-5xl font-bold tracking-tight mb-3">ShiftHappens</h1>
            <p className="text-zinc-400 max-w-xl">Conversations on global business, India's opportunities and the shifts shaping how companies grow across borders. Hosted by Srinagesh Mandala.</p>
          </div>
          <SubscribeButton testId="podcast-subscribe-btn" />
        </div>

        {videos === null ? (
          <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 text-[#C41E3A] animate-spin" /></div>
        ) : !current ? (
          <p className="text-zinc-500 py-12" data-testid="podcast-empty">New episodes coming soon. Subscribe on YouTube to be the first to watch.</p>
        ) : (
          <>
            <Player video={current} />
            <h2 className="text-base md:text-lg font-semibold mt-5 text-center" data-testid="podcast-current-title">{cleanTitle(current.title)}</h2>
            {videos.length > 1 && (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mt-12" data-testid="podcast-grid">
                {videos.map((v) => <Thumb key={v.id} video={v} active={v.id === current.id} onSelect={setCurrent} />)}
              </div>
            )}
          </>
        )}

        <div className="mt-16 border-t border-zinc-800 pt-10 flex flex-wrap items-center justify-between gap-6">
          <p className="text-zinc-400">More episodes and Shorts are on the ShiftHappens channel.</p>
          <a href={CHANNEL_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-white hover:text-[#C41E3A] font-medium transition-colors" data-testid="podcast-channel-link">
            Visit the channel <ArrowUpRight className="w-4 h-4" />
          </a>
        </div>
      </main>
    </div>
  );
}
